import { useContext } from "react";
import { ImperativeDialogContext, Input, SelectInput, type SelectOption } from "@repo/ui";
import { GenericFieldDialog, type FieldConfig } from "~/components/ui/generic-field-dialog";
import type { WorkflowState } from "~/types/api";
import type { WorkflowData } from "../workflows-page";
import { useRenameWorkflow } from "./apis/use-rename-workflow";
import { useUpdateWorkflowState } from "./apis/use-update-workflow-state";

const workflowStateOptions: SelectOption<WorkflowState>[] = [
  { value: "WorkInProgress", label: "Work In Progress" },
  { value: "Active", label: "Active" },
  { value: "Completed", label: "Completed" },
];

//Used for displaying workflow dialogs for editing simple dialog with inputs only like name and status.
//Not used for creating workflows or editing metadata/statuses, which have their own dialogs.
export const useWorkflowFieldDialog = () => {
  const dialogContext = useContext(ImperativeDialogContext);
  const { closeDialog, showDialog } = dialogContext ?? {};
  const { mutateAsync: renameWorkflow } = useRenameWorkflow();
  const { mutateAsync: updateWorkflowState } = useUpdateWorkflowState();

  const showRenameDialog = (workflow: WorkflowData) => {
    const fields: FieldConfig<string>[] = [
      {
        name: "name",
        label: "Package Name",
        defaultValue: workflow.name,
        render: (value, onChange, disabled) => (
          <Input
            type="text"
            value={value}
            inputSize="sm"
            className="font-medium"
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            autoFocus
          />
        ),
        validate: (value: string) => {
          const trimmed = value.trim();
          if (!trimmed) {
            return "Package name is required";
          }
          if (trimmed === workflow.name) {
            return "Package name must be different";
          }
          return null;
        },
      },
    ];

    showDialog?.({
      content: (
        <GenericFieldDialog
          title="Change Workflow Package Name"
          fields={fields}
          onSave={async (data: { name: string }) => {
            await renameWorkflow({ workflowId: workflow.id, name: data.name.trim() });
          }}
          onClose={closeDialog}
        />
      ),
      onClose: closeDialog,
    });
  };

  const showStatusDialog = (workflow: WorkflowData, allWorkflows: WorkflowData[]) => {
    let allowedNextStates: WorkflowState[] = [];

    if (workflow.state === "Active") {
      allowedNextStates = ["Completed"];
    } else if (workflow.state === "WorkInProgress") {
      const hasNoPrevious =
        workflow.previousVersionId === null || workflow.previousVersionId === undefined;

      const previousWorkflow = allWorkflows.find((w) => w.id === workflow.previousVersionId);
      const previousIsNotActive = previousWorkflow ? previousWorkflow.state !== "Active" : true;

      if (hasNoPrevious || previousIsNotActive) {
        allowedNextStates = ["Active", "Completed"];
      } else {
        allowedNextStates = ["Completed"];
      }
    }

    const availableOptions = workflowStateOptions.filter((option) =>
      allowedNextStates.includes(option.value)
    );

    const fields: FieldConfig<string>[] = [
      {
        name: "state",
        label: "Status",
        defaultValue: workflow.state,
        render: (value, onChange, disabled) => (
          <SelectInput<WorkflowState>
            value={value as WorkflowState}
            onChange={(v) => onChange((v as string) ?? "")}
            options={availableOptions}
            disabled={disabled || availableOptions.length === 0}
            placeholder={
              availableOptions.length === 0 ? "No transitions available" : "Select status"
            }
          />
        ),
        validate: (value: string) => {
          if (!value) return "Status is required";
          if (value === workflow.state) return "Status must be different";
          if (!allowedNextStates.includes(value as WorkflowState))
            return "Invalid status transition";
          return null;
        },
      },
    ];

    showDialog?.({
      content: (
        <GenericFieldDialog
          title="Change Workflow Package Status"
          fields={fields}
          onSave={async (data: { state: WorkflowState }) => {
            await updateWorkflowState({ workflowId: workflow.id, state: data.state });
          }}
          onClose={closeDialog}
        />
      ),
      onClose: closeDialog,
    });
  };

  return { showRenameDialog, showStatusDialog, closeDialog };
};
